import * as React from 'react';
import { useSelector } from 'react-redux';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { Divider } from '@lightspeed/flame/Divider';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import { ModalFooter as FlameModalFooter } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { getStore } from 'pages/store/selectors/employeeShops';

const StyledTextLink = styled(TextLink)`
  text-decoration: underline;
  margin-left: 3px; // fixes translations that trim empty spaces
`;

const StyledFooter = styled(FlameModalFooter)`
  background-color: ${theme.colors.white};
  padding: ${theme.space[0]};
`;

interface Props {
  onClickCancel: () => void;
  onClickSave: () => void;
  readOnly?: boolean;
}

const ModalFooter: React.FC<Props> = ({ onClickCancel, onClickSave, readOnly = false }) => {
  const { shops } = useSelector(getStore);
  const employeeShops = shops.filter((shop) => shop.hasAccess);
  const canSave = !!employeeShops.length;

  const handleOnClickLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (window.isGriffin()) {
      window.navigateToContent('listing', 'admin.listings.employee_roles');
      return;
    }
    window.open('/?name=admin.listings.employee_roles&form_name=listing', '_blank');
  };

  return (
    <StyledFooter>
      {!readOnly && (
        <>
          <Flex justifyContent="center" alignItems="center" px={1} py={2}>
            <IconInfo
              color={theme.colors['gray-600']}
              css={{ marginRight: theme.space[1] }}
              size="14px"
            />

            <Text size="small" color={theme.colors['gray-600']}>
              To restrict access to quantity-on-hand levels by location, go to
            </Text>

            <StyledTextLink
              color={theme.colors['gray-600']}
              data-test="link-to-employee-roles"
              size="small"
              onClick={handleOnClickLink}
            >
              Roles
            </StyledTextLink>
          </Flex>

          <Divider />
        </>
      )}
      <Flex justifyContent="flex-end" py={2} px={3}>
        <Button
          data-test="cancel-btn"
          aria-label="button-cancel-modal"
          fill
          onClick={onClickCancel}
          style={{ marginRight: theme.space[2] }}
        >
          Cancel
        </Button>
        {!readOnly && (
          <Button
            data-test="save-btn"
            aria-label="button-save-modal"
            disabled={!canSave}
            fill
            onClick={onClickSave}
            variant="secondary"
          >
            Save
          </Button>
        )}
      </Flex>
    </StyledFooter>
  );
};

export default ModalFooter;
